import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";

function SendConfirmModal({ open, onClose, onConfirm, loading, audienceCount }) {
  const [sendMode, setSendMode] = useState("immediate");
  const [scheduledFor, setScheduledFor] = useState("");
  const [minScheduleValue, setMinScheduleValue] = useState("");

  useEffect(() => {
    if (open) {
      setSendMode("immediate");
      setScheduledFor("");
      const minDate = new Date(Date.now() + 5 * 60 * 1000);
      const tzOffsetMs = minDate.getTimezoneOffset() * 60 * 1000;
      setMinScheduleValue(new Date(minDate.getTime() - tzOffsetMs).toISOString().slice(0, 16));
    }
  }, [open]);

  const canConfirm = useMemo(() => {
    if (sendMode === "immediate") return true;
    return Boolean(scheduledFor);
  }, [sendMode, scheduledFor]);

  const handleConfirm = () => {
    const scheduledIso =
      sendMode === "scheduled" && scheduledFor
        ? (() => {
            const date = new Date(scheduledFor);
            // datetime-local is minute precision; push to end of minute to avoid
            // false "past" failures around second boundaries.
            date.setSeconds(59, 0);
            return date.toISOString();
          })()
        : null;
    onConfirm({
      send_mode: sendMode,
      scheduled_for: scheduledIso,
    });
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send Newsletter</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          This will send the approved newsletter to approximately {audienceCount || 0} recipients.
          Make sure the subject, preview, and audience are correct before continuing.
        </Typography>

        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={sendMode}
            onChange={(e) => setSendMode(e.target.value)}
          >
            <FormControlLabel
              value="immediate"
              control={<Radio />}
              label="Send now"
              disabled={loading}
            />
            <FormControlLabel
              value="scheduled"
              control={<Radio />}
              label="Schedule for later"
              disabled={loading}
            />
          </RadioGroup>
        </FormControl>

        {sendMode === "scheduled" ? (
          <TextField
            margin="dense"
            type="datetime-local"
            fullWidth
            label="Schedule Date & Time"
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            inputProps={{ min: minScheduleValue }}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            helperText="Zoho Campaign will schedule this newsletter for the selected date and time."
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !canConfirm}>
          {loading
            ? sendMode === "scheduled"
              ? "Scheduling..."
              : "Sending..."
            : sendMode === "scheduled"
              ? "Schedule Newsletter"
              : "Send Newsletter"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SendConfirmModal;
